/* ==========================================================================
   OPV — Base behaviours
   Custom elements autoconstruibles: funcionan al cargar la página y también
   cuando el editor de temas vuelve a renderizar una sección.
   ========================================================================== */
(function () {
  'use strict';

  // El snippet opv-assets puede inyectarse desde varias secciones; solo booteamos una vez.
  if (window.OPV && window.OPV.booted) return;
  window.OPV = window.OPV || {};
  window.OPV.booted = true;

  /* Formatea centavos con el `shop.money_format` de la tienda.
     La versión anterior no agrupaba los miles: en Colombia mostraba
     "$298000,00" en vez de "$298.000,00". Aquí se replican los mismos
     marcadores que usa Shopify en el servidor, con su agrupación. */
  var money = function (cents, format) {
    format = format || '${{amount}}';
    var num = (Number(cents) || 0) / 100;
    var negativo = num < 0;

    function armar(sepMiles, sepDecimal, decimales) {
      var fijo = Math.abs(num).toFixed(decimales);
      var trozos = fijo.split('.');
      var entero = trozos[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMiles);
      var salida = decimales > 0 ? entero + sepDecimal + trozos[1] : entero;
      return (negativo ? '-' : '') + salida;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, clave) {
      switch (clave) {
        case 'amount_no_decimals':                      return armar(',', '.', 0);
        case 'amount_with_comma_separator':             return armar('.', ',', 2);
        case 'amount_no_decimals_with_comma_separator': return armar('.', ',', 0);
        case 'amount_with_apostrophe_separator':        return armar("'", '.', 2);
        case 'amount_with_space_separator':             return armar(' ', ',', 2);
        case 'amount_no_decimals_with_space_separator': return armar(' ', ',', 0);
        default:                                        return armar(',', '.', 2);
      }
    });
  };
  window.OPV = window.OPV || {};
  window.OPV.money = money;
  window.OPV.moneyFormat = window.OPV.moneyFormat || '${{amount}}';

  /* ---------------------------------------------------------------------
     Reveal al hacer scroll
     --------------------------------------------------------------------- */
  var revealObserver = null;
  function initReveal(root) {
    if (!('IntersectionObserver' in window)) {
      (root || document).querySelectorAll('.opv-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    (root || document).querySelectorAll('.opv-reveal:not(.is-visible)').forEach(function (el) {
      revealObserver.observe(el);
    });
  }
  window.OPV.initReveal = initReveal;

  /* ---------------------------------------------------------------------
     <opv-accordion> — acordeón con transición de altura
     --------------------------------------------------------------------- */
  class OpvAccordionEl extends HTMLElement {
    connectedCallback() {
      this.single = this.dataset.single === 'true';
      this.items = Array.from(this.querySelectorAll('[data-opv-accordion-item]'));
      this.items.forEach(this.setup.bind(this));
      window.addEventListener('resize', this.sync.bind(this));
    }
    setup(item) {
      var trigger = item.querySelector('.opv-accordion__trigger');
      var panel = item.querySelector('.opv-accordion__panel');
      if (!trigger || !panel) return;
      if (trigger.getAttribute('aria-expanded') === 'true') {
        panel.style.height = 'auto';
      }
      trigger.addEventListener('click', function () {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (this.single && !isOpen) {
          this.items.forEach(function (other) {
            if (other !== item) this.close(other);
          }, this);
        }
        isOpen ? this.close(item) : this.open(item);
      }.bind(this));
    }
    open(item) {
      var trigger = item.querySelector('.opv-accordion__trigger');
      var panel = item.querySelector('.opv-accordion__panel');
      trigger.setAttribute('aria-expanded', 'true');
      panel.style.height = panel.scrollHeight + 'px';
      panel.addEventListener('transitionend', function handler(e) {
        if (e.propertyName !== 'height') return;
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', handler);
      });
    }
    close(item) {
      var trigger = item.querySelector('.opv-accordion__trigger');
      var panel = item.querySelector('.opv-accordion__panel');
      if (trigger.getAttribute('aria-expanded') !== 'true') return;
      trigger.setAttribute('aria-expanded', 'false');
      panel.style.height = panel.scrollHeight + 'px';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { panel.style.height = '0px'; });
      });
    }
    sync() {
      this.items.forEach(function (item) {
        var trigger = item.querySelector('.opv-accordion__trigger');
        var panel = item.querySelector('.opv-accordion__panel');
        if (trigger && panel && trigger.getAttribute('aria-expanded') === 'true') {
          panel.style.height = 'auto';
        }
      });
    }
  }
  if (!customElements.get('opv-accordion')) customElements.define('opv-accordion', OpvAccordionEl);

  /* ---------------------------------------------------------------------
     <opv-tabs> — pestañas (ej. Cápsula / Gomita)
     --------------------------------------------------------------------- */
  class OpvTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach(function (tab, i) {
        tab.addEventListener('click', this.select.bind(this, i));
        tab.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') this.select((i + 1) % this.tabs.length, e);
          if (e.key === 'ArrowLeft') this.select((i - 1 + this.tabs.length) % this.tabs.length, e);
        }.bind(this));
      }, this);

      // Flechas opcionales para saltar de pestaña
      var step = function (dir) {
        var current = this.tabs.findIndex(function (t) { return t.getAttribute('aria-selected') === 'true'; });
        if (current < 0) current = 0;
        this.select((current + dir + this.tabs.length) % this.tabs.length);
      }.bind(this);
      this.querySelectorAll('[data-opv-tab-prev]').forEach(function (b) {
        b.addEventListener('click', function () { step(-1); });
      });
      this.querySelectorAll('[data-opv-tab-next]').forEach(function (b) {
        b.addEventListener('click', function () { step(1); });
      });
    }
    select(index, event) {
      if (event) event.preventDefault();
      this.tabs.forEach(function (tab, i) {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.setAttribute('tabindex', i === index ? '0' : '-1');
      });
      this.panels.forEach(function (panel, i) { panel.hidden = i !== index; });
      if (event) this.tabs[index].focus();
      // Reajusta acordeones abiertos que contengan estas tabs
      var panel = this.closest('.opv-accordion__panel');
      if (panel && panel.style.height !== '0px') panel.style.height = 'auto';
    }
  }
  if (!customElements.get('opv-tabs')) customElements.define('opv-tabs', OpvTabs);

  /* ---------------------------------------------------------------------
     <opv-carousel> — carrusel scroll-snap con flechas, puntos y autoplay
     --------------------------------------------------------------------- */
  class OpvCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.opv-carousel__viewport');
      if (!this.viewport) return;
      this.slides = Array.from(this.viewport.children);
      this.prevBtn = this.querySelector('[data-opv-prev]');
      this.nextBtn = this.querySelector('[data-opv-next]');
      this.dots = Array.from(this.querySelectorAll('[data-opv-dot]'));
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = parseInt(this.dataset.interval || '5000', 10);
      this.loop = this.dataset.loop !== 'false';

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.step.bind(this, -1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.step.bind(this, 1));
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', this.goTo.bind(this, i));
      }, this);

      this.viewport.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.update();

      if (this.autoplay) {
        this.addEventListener('mouseenter', this.stop.bind(this));
        this.addEventListener('mouseleave', this.start.bind(this));
        this.addEventListener('focusin', this.stop.bind(this));
        this.viewport.addEventListener('touchstart', this.stop.bind(this), { passive: true });
        this.start();
      }
    }
    disconnectedCallback() { this.stop(); }
    get index() {
      var pos = this.viewport.scrollLeft;
      var best = 0, bestDist = Infinity;
      this.slides.forEach(function (slide, i) {
        var d = Math.abs(slide.offsetLeft - this.viewport.offsetLeft - pos);
        if (d < bestDist) { bestDist = d; best = i; }
      }, this);
      return best;
    }
    goTo(i, event) {
      if (event) event.preventDefault();
      var slide = this.slides[i];
      if (!slide) return;
      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
    }
    step(dir) {
      var next = this.index + dir;
      var max = this.slides.length - 1;
      if (next > max) next = this.loop ? 0 : max;
      if (next < 0) next = this.loop ? max : 0;
      this.goTo(next);
    }
    onScroll() {
      clearTimeout(this._raf);
      this._raf = setTimeout(this.update.bind(this), 80);
    }
    update() {
      var i = this.index;
      this.dots.forEach(function (dot, d) { dot.classList.toggle('is-active', d === i); });
      if (!this.loop) {
        var atStart = this.viewport.scrollLeft <= 2;
        var atEnd = this.viewport.scrollLeft + this.viewport.clientWidth >= this.viewport.scrollWidth - 2;
        if (this.prevBtn) this.prevBtn.disabled = atStart;
        if (this.nextBtn) this.nextBtn.disabled = atEnd;
      }
    }
    start() {
      this.stop();
      if (!this.autoplay) return;
      this._timer = setInterval(this.step.bind(this, 1), this.interval);
    }
    stop() { if (this._timer) clearInterval(this._timer); }
  }
  if (!customElements.get('opv-carousel')) customElements.define('opv-carousel', OpvCarousel);

  /* ---------------------------------------------------------------------
     <opv-marquee> — duplica el contenido para un loop continuo
     --------------------------------------------------------------------- */
  class OpvMarquee extends HTMLElement {
    connectedCallback() {
      var track = this.querySelector('.opv-marquee__track');
      if (!track || track.dataset.cloned === 'true') return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.cloned = 'true';
      track.dataset.cloned = 'true';
      this.appendChild(clone);
    }
  }
  if (!customElements.get('opv-marquee')) customElements.define('opv-marquee', OpvMarquee);

  /* ---------------------------------------------------------------------
     <opv-rotator> — mensajes que rotan (announcement bar)
     --------------------------------------------------------------------- */
  class OpvRotator extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.children);
      if (this.items.length < 2) return;
      this.i = 0;
      this.speed = parseInt(this.dataset.speed || '4000', 10);
      this.items.forEach(function (el, i) {
        el.style.transition = 'opacity .45s ease, transform .45s ease';
        if (i !== 0) { el.style.display = 'none'; el.style.opacity = '0'; }
      });
      this._timer = setInterval(this.next.bind(this), this.speed);
    }
    disconnectedCallback() { clearInterval(this._timer); }
    next() {
      var current = this.items[this.i];
      this.i = (this.i + 1) % this.items.length;
      var next = this.items[this.i];
      current.style.opacity = '0';
      current.style.transform = 'translateY(-6px)';
      setTimeout(function () {
        current.style.display = 'none';
        next.style.display = '';
        next.style.transform = 'translateY(6px)';
        requestAnimationFrame(function () {
          next.style.opacity = '1';
          next.style.transform = 'none';
        });
      }, 400);
    }
  }
  if (!customElements.get('opv-rotator')) customElements.define('opv-rotator', OpvRotator);

  /* ---------------------------------------------------------------------
     <opv-drawer> — panel lateral genérico (cart drawer, menú móvil)
     --------------------------------------------------------------------- */
  class OpvDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-opv-overlay]');
      this.panel = this.querySelector('[data-opv-panel]');
      this.querySelectorAll('[data-opv-close]').forEach(function (btn) {
        btn.addEventListener('click', this.close.bind(this));
      }, this);
      if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
      }.bind(this));
    }
    open() {
      this.setAttribute('open', '');
      document.body.classList.add('opv-scroll-lock');
      var focusable = this.querySelector('[data-opv-close]');
      if (focusable) setTimeout(function () { focusable.focus(); }, 60);
      this.dispatchEvent(new CustomEvent('opv:drawer:open', { bubbles: true }));
    }
    close() {
      this.removeAttribute('open');
      document.body.classList.remove('opv-scroll-lock');
    }
    toggle() { this.hasAttribute('open') ? this.close() : this.open(); }
  }
  if (!customElements.get('opv-drawer')) customElements.define('opv-drawer', OpvDrawer);

  /* ---------------------------------------------------------------------
     Cart API helpers
     --------------------------------------------------------------------- */
  var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function refreshCart() {
    return fetch(routes + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-opv-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.classList.toggle('is-empty', cart.item_count === 0);
          el.classList.remove('is-bumped');
          void el.offsetWidth;
          el.classList.add('is-bumped');
        });
        document.dispatchEvent(new CustomEvent('opv:cart:updated', { detail: { cart: cart } }));
        return cart;
      });
  }
  window.OPV.refreshCart = refreshCart;

  function renderCartDrawer() {
    var drawer = document.querySelector('[data-opv-cart-drawer]');
    if (!drawer) return Promise.resolve();
    var sectionId = drawer.dataset.sectionId;
    return fetch(routes + '?sections=' + sectionId)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[sectionId];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-opv-cart-contents]');
        var target = drawer.querySelector('[data-opv-cart-contents]');
        if (fresh && target) target.innerHTML = fresh.innerHTML;
      });
  }
  window.OPV.renderCartDrawer = renderCartDrawer;

  function addToCart(payload, button) {
    if (button) button.classList.add('is-loading');
    return fetch(routes + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.body.description || res.body.message || 'Error');
        return Promise.all([refreshCart(), renderCartDrawer()]).then(function () {
          var drawer = document.querySelector('[data-opv-cart-drawer]');
          if (drawer && drawer.dataset.autoOpen !== 'false') drawer.open();
          return res.body;
        });
      })
      .finally(function () { if (button) button.classList.remove('is-loading'); });
  }
  window.OPV.addToCart = addToCart;

  function changeCartLine(key, quantity, el) {
    if (el) el.classList.add('is-loading');
    return fetch(routes + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return Promise.all([refreshCart(), renderCartDrawer()]); })
      .finally(function () { if (el) el.classList.remove('is-loading'); });
  }
  window.OPV.changeCartLine = changeCartLine;

  /* Delegación global: abrir drawer, quick add, líneas del carrito */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-opv-open-drawer]');
    if (opener) {
      var target = document.querySelector(opener.getAttribute('data-opv-open-drawer'));
      if (target && target.open) { e.preventDefault(); target.open(); }
      return;
    }
    var quickAdd = e.target.closest('[data-opv-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      var body = { items: [{ id: parseInt(quickAdd.dataset.opvQuickAdd, 10), quantity: 1 }] };
      if (quickAdd.dataset.sellingPlan) body.items[0].selling_plan = parseInt(quickAdd.dataset.sellingPlan, 10);
      addToCart(body, quickAdd).catch(function (err) { alert(err.message); });
      return;
    }
    var lineBtn = e.target.closest('[data-opv-line-change]');
    if (lineBtn) {
      e.preventDefault();
      changeCartLine(lineBtn.dataset.lineKey, parseInt(lineBtn.dataset.opvLineChange, 10), lineBtn);
    }
  });

  /* ---------------------------------------------------------------------
     Header sticky
     --------------------------------------------------------------------- */
  function initStickyHeader() {
    var header = document.querySelector('[data-opv-header]');
    if (!header) return;
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------------------------------------------------------------
     Boot + reinicialización en el editor de temas
     --------------------------------------------------------------------- */
  function boot(root) {
    initReveal(root);
    initStickyHeader();
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) {
    var drawer = e.target.querySelector('opv-drawer[data-opv-cart-drawer]');
    if (drawer) drawer.open();
  });
})();
